///
/// EarthBrowser
///
/// Created by Matt Giger on 11/20/2023
/// Copyright (c) 2023 Geodata Labs
///

import { type DrawMode } from "@stovokor/shader/GLConstants"
import { type ShaderAction } from "@stovokor/shader/ShaderActions"
import { adjustBufferMemory } from "@stovokor/shader/TextureMemory"
import { StovokorContext } from "@stovokor/StovokorContext"

// Defines a group of elements to be rendered within a buffer
export class ElementGroup {
	constructor(public count: number, public offset: number) {}
}

type indexBufferType = Uint32Array | Uint16Array | Int16Array

export class IndexBuffer {
	public count: number = 0
	public groups: ElementGroup[] = []
	public unpurgable: boolean = false
	/** Bumped when a new GL buffer object is created; see VertexBuffer.generation */
	public generation: number = 0
	private buffer: WebGLBuffer | null = null
	private type: GLenum = WebGL2RenderingContext.UNSIGNED_INT
	private values: number[] = []
	private array: indexBufferType | undefined
	private allocatedBytes: number = 0
	private context: StovokorContext

	constructor(context	: StovokorContext, data: number[] | indexBufferType | undefined = undefined) {
		this.context = context
		if(Array.isArray(data)) {
			this.values = data	// allow for indices to be built up from an external source with the line, triangle, quad methods
		} else if(data instanceof Uint16Array) {
			this.type = WebGL2RenderingContext.UNSIGNED_SHORT
			this.array = data
		} else if(data instanceof Int16Array) {
			this.type = WebGL2RenderingContext.SHORT
			this.array = data
		} else if(data instanceof Uint32Array) {
			this.type = WebGL2RenderingContext.UNSIGNED_INT
			this.array = data
		}
	}

	public destroy(): void {
		if(!this.unpurgable && this.buffer) {
			this.context.gl.deleteBuffer(this.buffer)
			this.buffer = null
			this.account(0)
		}
	}

	/** Mirror this buffer's GPU bytes into the global memory counters */
	private account(bytes: number): void {
		if(bytes !== this.allocatedBytes) {
			adjustBufferMemory(bytes - this.allocatedBytes, (bytes > 0 ? 1 : 0) - (this.allocatedBytes > 0 ? 1 : 0))
			this.allocatedBytes = bytes
		}
	}

	public index(value: number): void {
		this.values.push(value)
	}

	public line(a: number, b: number): void {
		this.values.push(a, b)
	}

	public triangle(x: number, y: number, z: number): void {
		this.values.push(x, y, z)
	}

	public quad(a: number, b: number, c: number, d: number): void {
		this.values.push(a, b, c, c, d, a)
	}

	/**
	 * Replace the index set. The data is uploaded on the next bind, so the
	 * draw count and element type follow it; an empty array draws nothing.
	 */
	public uploadData(indices: Uint32Array | Uint16Array): void {
		this.type = indices instanceof Uint16Array ? WebGL2RenderingContext.UNSIGNED_SHORT : WebGL2RenderingContext.UNSIGNED_INT
		this.array = indices
		this.values = []
	}

	/** True while indices are waiting to go to the GPU on the next bind */
	public get pending(): boolean {
		return this.array !== undefined || this.values.length > 0
	}

	public bind(): void {
		const pending = this.pending
		if(this.buffer === null && pending) {
			this.buffer = this.context.gl.createBuffer()
			this.generation++
		}
		this.context.gl.bindBuffer(WebGL2RenderingContext.ELEMENT_ARRAY_BUFFER, this.buffer)
		if(pending) {
			if(!this.array) {
				this.array = new Uint32Array(this.values)
			}
			this.count = this.array.length
			this.context.gl.bufferData(WebGL2RenderingContext.ELEMENT_ARRAY_BUFFER, this.array, WebGL2RenderingContext.STATIC_DRAW)
			this.account(this.array.byteLength)
			this.array = undefined
			this.values = []
		}
	}

	public unbind(): void {
		this.context.gl.bindBuffer(WebGL2RenderingContext.ELEMENT_ARRAY_BUFFER, null)
	}

	public bindAction(): ShaderAction {
		return () => this.bind()
	}

	public unbindAction(): ShaderAction {
		return () => this.unbind()
	}

	public drawAction(mode: DrawMode): ShaderAction {
		return () => this.context.gl.drawElements(mode, this.count, this.type, 0)
	}

	/** Instanced draw; the count is read when the action runs, so it may change per frame */
	public drawInstancedAction(mode: DrawMode, instanceCount: () => number): ShaderAction {
		return () => {
			const instances = instanceCount()
			if(instances > 0) {
				this.context.gl.drawElementsInstanced(mode, this.count, this.type, 0, instances)
			}
		}
	}

	public drawGroupAction(index: number, mode: DrawMode): ShaderAction {
		const group = this.groups[index]
		return () => this.context.gl.drawElements(mode, group.count, this.type, group.offset * 4)
	}

	public draw(mode: DrawMode): void {
		this.context.gl.drawElements(mode, this.count, this.type, 0)
	}

	public drawGroup(index: number, mode: DrawMode): void {
		const group = this.groups[index]
		this.context.gl.drawElements(mode, group.count, this.type, group.offset * 4)
	}
}